// @ts-nocheck
import React, { useEffect } from 'react';
import { ArrowRightIcon, Sparkles, PhoneIcon, MailIcon, GlobeIcon, MapPinIcon, StarIcon } from 'lucide-react';
import { ClinicSettings } from '../types';

// --- PROPS INTERFACE ---
interface BrochurePDFProps {
    reportData: any;
    originalImageUrl: string;
    generatedImageUrl: string;
    smileRating: any;
    afterSmileRating: any;
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
    clinicSettings?: ClinicSettings;
    patientName?: string;
    onRendered: () => void;
}

// --- GLOBAL STYLES & FONTS ---
const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@200;300;400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Alex+Brush&display=swap');
        
        .pdf-page-brochure {
            font-feature-settings: "ss01", "ss02";
            -webkit-font-smoothing: antialiased;
        }

        .font-signature {
            font-family: 'Alex Brush', cursive;
        }
    `}</style>
);

// --- TREATMENT CONFIGURATION ---
const treatmentConfigs: Record<string, { color: string; label: string; gradient: string }> = {
    cosmetic: { color: '#14B8A6', label: 'Cosmetic Enhancement', gradient: 'from-teal-500 to-emerald-600' },
    orthodontic: { color: '#8B5CF6', label: 'Orthodontic Alignment', gradient: 'from-violet-500 to-purple-600' },
    restorative: { color: '#F59E0B', label: 'Restorative Treatment', gradient: 'from-amber-500 to-orange-600' },
    'pro-automated': { color: '#EC4899', label: 'Pro Transformation', gradient: 'from-pink-500 to-rose-600' },
    'fast-pro': { color: '#06B6D4', label: 'Fast Pro', gradient: 'from-cyan-500 to-teal-600' }
};

// --- ICON COMPONENT ---
const FuturaSmileIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3L13.06 11.06L21 12L13.06 12.94L12 21L10.94 12.94L3 12L10.94 11.06L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

// --- PAGE COMPONENT ---
const Page: React.FC<{ children: React.ReactNode; className?: string; bgColor?: string }> = ({ children, className = '', bgColor = '#FAFAFA' }) => (
    <div
        className={`pdf-page-brochure relative w-[210mm] h-[297mm] text-[#1E293B] flex flex-col font-sans overflow-hidden ${className}`}
        style={{
            fontFamily: "'Inter', sans-serif",
            pageBreakAfter: 'always',
            background: bgColor
        }}
    >
        {children}
    </div>
);

// --- SCORE RING COMPONENT ---
const ScoreRing: React.FC<{ score: number; label: string; size?: number; color?: string }> = ({ score, label, size = 140, color = '#14B8A6' }) => {
    const strokeWidth = 6;
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
        <div className="flex flex-col items-center">
            <div className="relative" style={{ width: size, height: size }}>
                <svg className="w-full h-full -rotate-90 relative z-10" viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" style={{ strokeDasharray: circumference, strokeDashoffset: offset }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-light text-slate-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{score}</span>
                    <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">/100</span>
                </div>
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
        </div>
    );
};

// --- RADAR CHART COMPONENT ---
const RadarChart: React.FC<{ before: any[]; after: any[]; color: string }> = ({ before, after, color }) => {
    // Guard against empty or invalid data
    if (!before?.length || !after?.length || before.length < 3) return null;

    const size = 240;
    const center = size / 2;
    const radius = center * 0.7;
    const points = (data: any[]) => data.map((d, i) => {
        const r = radius * ((d.score ?? 0) / 100);
        const a = (Math.PI * 2 * i) / data.length - Math.PI / 2;
        return `${center + r * Math.cos(a)},${center + r * Math.sin(a)}`;
    }).join(' ');

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {[0.25, 0.5, 0.75, 1].map((s, i) => (
                <circle key={i} cx={center} cy={center} r={radius * s} fill="none" stroke="#E2E8F0" strokeDasharray="3 3" />
            ))}
            <polygon points={points(before)} fill="#94A3B8" fillOpacity="0.15" stroke="#94A3B8" strokeWidth="1.5" />
            <polygon points={points(after)} fill={color} fillOpacity="0.25" stroke={color} strokeWidth="2" />
            {before.map((d, i) => {
                const a = (Math.PI * 2 * i) / before.length - Math.PI / 2;
                const x = center + radius * 1.25 * Math.cos(a);
                const y = center + radius * 1.25 * Math.sin(a);
                return (
                    <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="#64748B" fontWeight="600" letterSpacing="0.05em">
                        {d.label.toUpperCase()}
                    </text>
                );
            })}
        </svg>
    );
};

// --- METRIC BAR COMPONENT ---
const MetricBar: React.FC<{ label: string; before: number; after: number; color?: string }> = ({ label, before, after, color = '#14B8A6' }) => (
    <div className="w-full mb-4">
        <div className="flex justify-between items-center mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
            <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{before}</span>
                <ArrowRightIcon className="w-3 h-3 text-slate-300" />
                <span className="text-sm font-bold" style={{ color }}>{after}</span>
            </div>
        </div>
        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden relative">
            <div className="absolute h-full bg-slate-300 rounded-full" style={{ width: `${before}%` }} />
            <div className="absolute h-full rounded-full shadow-sm" style={{ width: `${after}%`, backgroundColor: color }} />
        </div>
    </div>
);

// --- MAIN COMPONENT ---
const BrochurePDF: React.FC<BrochurePDFProps> = ({
    reportData, originalImageUrl, generatedImageUrl, smileRating, afterSmileRating, treatmentType, clinicSettings, patientName, onRendered
}) => {
    useEffect(() => { setTimeout(onRendered, 800); }, [onRendered]);

    const primaryColor = clinicSettings?.primaryColor || treatmentConfigs[treatmentType]?.color || '#14B8A6';
    const treatmentLabel = treatmentConfigs[treatmentType]?.label || 'Smile Enhancement';
    const treatmentGradient = treatmentConfigs[treatmentType]?.gradient || 'from-teal-500 to-emerald-600';

    const scoreBefore = smileRating?.overallSmileScore?.score ?? 0;
    const scoreAfter = afterSmileRating?.overallSmileScore?.score ?? 0;
    const improvement = scoreAfter - scoreBefore;

    const radarBefore = [
        { label: 'Symmetry', score: smileRating?.detailedScores?.symmetryAndAlignment ?? 50 },
        { label: 'Brightness', score: smileRating?.detailedScores?.colorAndBrightness ?? 50 },
        { label: 'Proportions', score: smileRating?.detailedScores?.shapeAndProportions ?? 50 },
        { label: 'Gum Line', score: smileRating?.detailedScores?.gumHealthAndContour ?? 50 },
    ];
    const radarAfter = [
        { label: 'Symmetry', score: afterSmileRating?.detailedScores?.symmetryAndAlignment ?? 50 },
        { label: 'Brightness', score: afterSmileRating?.detailedScores?.colorAndBrightness ?? 50 },
        { label: 'Proportions', score: afterSmileRating?.detailedScores?.shapeAndProportions ?? 50 },
        { label: 'Gum Line', score: afterSmileRating?.detailedScores?.gumHealthAndContour ?? 50 },
    ];

    const clinicLogo = clinicSettings?.clinicLogoBase64
        ? `data:${clinicSettings.clinicLogoMimeType || 'image/png'};base64,${clinicSettings.clinicLogoBase64}`
        : null;

    return (
        <div style={{ background: '#FAFAFA', color: '#1E293B' }}>
            <GlobalStyles />

            {/* ========== PAGE 1: CLINIC PROFILE ========== */}
            <Page bgColor="#FFFFFF">
                {/* Decorative Background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-slate-50 to-slate-100" />
                    <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-slate-50 to-slate-100" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-slate-100" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-slate-50" />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full px-[20mm] py-[25mm]">
                    {/* Logo */}
                    <div className="mb-10">
                        {clinicLogo ? (
                            <img src={clinicLogo} alt="Clinic Logo" className="max-h-28 max-w-[200px] object-contain" />
                        ) : (
                            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-xl">
                                <FuturaSmileIcon className="w-12 h-12 text-white" />
                            </div>
                        )}
                    </div>

                    {/* Clinic Name */}
                    <h1 className="text-5xl text-center text-slate-900 mb-4 leading-tight" style={{ fontFamily: "'DM Serif Display', serif" }}>
                        {clinicSettings?.clinicName || 'Premier Dental Practice'}
                    </h1>

                    {/* Doctor Name */}
                    {clinicSettings?.doctorName && (
                        <p className="text-2xl text-slate-600 font-signature mb-8">{clinicSettings.doctorName}</p>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-8">
                        <div className="w-16 h-px bg-slate-300" />
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                        <div className="w-16 h-px bg-slate-300" />
                    </div>

                    {/* Treatment Type Badge */}
                    <div className="px-6 py-3 rounded-full bg-gradient-to-r shadow-lg mb-10" style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}10)`, border: `1px solid ${primaryColor}30` }}>
                        <span className="text-sm font-bold uppercase tracking-widest" style={{ color: primaryColor }}>{treatmentLabel}</span>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-4 text-center">
                        {clinicSettings?.clinicAddress && (
                            <div className="flex items-center justify-center gap-3 text-slate-500">
                                <MapPinIcon className="w-4 h-4 text-slate-400" />
                                <span className="text-sm">{clinicSettings.clinicAddress}</span>
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-6">
                            {clinicSettings?.clinicPhone && (
                                <div className="flex items-center gap-2 text-slate-500">
                                    <PhoneIcon className="w-4 h-4 text-slate-400" />
                                    <span className="text-sm">{clinicSettings.clinicPhone}</span>
                                </div>
                            )}
                            {clinicSettings?.clinicEmail && (
                                <div className="flex items-center gap-2 text-slate-500">
                                    <MailIcon className="w-4 h-4 text-slate-400" />
                                    <span className="text-sm">{clinicSettings.clinicEmail}</span>
                                </div>
                            )}
                        </div>
                        {clinicSettings?.clinicWebsite && (
                            <div className="flex items-center justify-center gap-2 text-slate-500">
                                <GlobeIcon className="w-4 h-4 text-slate-400" />
                                <span className="text-sm font-medium">{clinicSettings.clinicWebsite}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="absolute bottom-8 left-0 right-0 flex justify-center">
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-widest">
                        <FuturaSmileIcon className="w-3 h-3" />
                        <span>Powered by FuturaSmile.com</span>
                    </div>
                </div>
            </Page>

            {/* ========== PAGE 2: BEFORE IMAGE ========== */}
            <Page bgColor="#F8FAFC">
                {/* Header */}
                <div className="px-[20mm] pt-[15mm] pb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 mb-2">Current State</p>
                            <h2 className="text-3xl text-slate-800" style={{ fontFamily: "'DM Serif Display', serif" }}>Before Treatment</h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Patient</p>
                                <p className="text-sm font-medium text-slate-700">{patientName || reportData?.patientName || 'Valued Patient'}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                                <span className="text-lg font-bold text-slate-300">{scoreBefore}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Image Container */}
                <div className="flex-1 px-[20mm] pb-6 flex items-center justify-center">
                    <div className="relative">
                        {/* Decorative Frame */}
                        <div className="absolute -inset-4 rounded-3xl bg-white shadow-2xl border border-slate-100" />

                        {/* Image - 1:1 Ratio */}
                        <div className="relative w-[130mm] h-[130mm] rounded-2xl overflow-hidden bg-slate-200 shadow-inner">
                            <img
                                src={originalImageUrl}
                                alt="Before"
                                className="w-full h-full"
                                style={{ objectFit: 'cover' }}
                            />
                            {/* Label */}
                            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-white/50">
                                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-700">Initial State</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-[20mm] pb-[15mm]">
                    <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                        <p className="text-[9px] uppercase font-bold tracking-widest text-slate-400">{clinicSettings?.clinicName || 'FuturaSmile'}</p>
                        <p className="text-[10px] text-slate-400">Page 2 of 4</p>
                    </div>
                </div>
            </Page>

            {/* ========== PAGE 3: AFTER IMAGE ========== */}
            <Page bgColor="#F8FAFC">
                {/* Header */}
                <div className="px-[20mm] pt-[15mm] pb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: primaryColor }}>Transformation Complete</p>
                            <h2 className="text-3xl text-slate-800" style={{ fontFamily: "'DM Serif Display', serif" }}>After Treatment</h2>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Improvement Badge */}
                            <div className="px-4 py-2 rounded-full bg-gradient-to-r shadow-lg" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}>
                                <span className="text-white font-bold text-sm">+{improvement} Points</span>
                            </div>
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg border-2" style={{ backgroundColor: `${primaryColor}15`, borderColor: primaryColor }}>
                                <span className="text-xl font-bold" style={{ color: primaryColor }}>{scoreAfter}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Image Container */}
                <div className="flex-1 px-[20mm] pb-6 flex items-center justify-center">
                    <div className="relative">
                        {/* Decorative Frame with Accent */}
                        <div className="absolute -inset-4 rounded-3xl bg-white shadow-2xl" style={{ borderWidth: 2, borderColor: `${primaryColor}30` }} />

                        {/* Image - 1:1 Ratio */}
                        <div className="relative w-[130mm] h-[130mm] rounded-2xl overflow-hidden bg-slate-200 shadow-inner">
                            <img
                                src={generatedImageUrl}
                                alt="After"
                                className="w-full h-full"
                                style={{ objectFit: 'cover' }}
                            />
                            {/* Label */}
                            <div className="absolute bottom-4 right-4 px-4 py-2 rounded-full shadow-lg border border-white/30" style={{ backgroundColor: primaryColor }}>
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3 h-3 text-white" />
                                    <span className="text-[11px] font-bold uppercase tracking-widest text-white">Projected Result</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-[20mm] pb-[15mm]">
                    <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                        <p className="text-[9px] uppercase font-bold tracking-widest text-slate-400">{clinicSettings?.clinicName || 'FuturaSmile'}</p>
                        <p className="text-[10px] text-slate-400">Page 3 of 4</p>
                    </div>
                </div>
            </Page>

            {/* ========== PAGE 4: ANALYSIS & SCORES ========== */}
            <Page bgColor="#FFFFFF">
                {/* Header */}
                <div className="px-[20mm] pt-[15mm] pb-4">
                    <div className="text-center mb-2">
                        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 mb-2">Comprehensive Analysis</p>
                        <h2 className="text-3xl text-slate-800" style={{ fontFamily: "'DM Serif Display', serif" }}>Smile Transformation</h2>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 px-[20mm] pb-6">
                    {/* Score Comparison */}
                    <div className="flex justify-center gap-12 mb-8">
                        <ScoreRing score={scoreBefore} label="Before" size={110} color="#94A3B8" />
                        <div className="flex items-center">
                            <div className="flex flex-col items-center px-6">
                                <ArrowRightIcon className="w-8 h-8 text-slate-300 mb-2" />
                                <div className="px-4 py-2 rounded-full shadow-md" style={{ backgroundColor: primaryColor }}>
                                    <span className="text-white font-bold text-sm">+{improvement}</span>
                                </div>
                            </div>
                        </div>
                        <ScoreRing score={scoreAfter} label="After" size={110} color={primaryColor} />
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-2 gap-8">
                        {/* Left: Radar Chart */}
                        <div className="flex flex-col items-center">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Visual Analysis</p>
                            <RadarChart before={radarBefore} after={radarAfter} color={primaryColor} />
                            <div className="flex items-center gap-6 mt-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-slate-400" />
                                    <span className="text-[10px] text-slate-500 font-medium">Before</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryColor }} />
                                    <span className="text-[10px] text-slate-500 font-medium">After</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Metrics */}
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Detailed Metrics</p>
                            <div className="space-y-1">
                                {radarBefore.map((metric, idx) => (
                                    <MetricBar
                                        key={idx}
                                        label={metric.label}
                                        before={metric.score}
                                        after={radarAfter[idx]?.score || 0}
                                        color={primaryColor}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Key Improvements */}
                    {(reportData?.afterVision?.keyChanges || []).length > 0 && (
                        <div className="mt-6 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Key Improvements</p>
                            <div className="grid grid-cols-2 gap-2">
                                {(reportData?.afterVision?.keyChanges || []).slice(0, 4).map((change: string, idx: number) => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                                        <span className="text-xs text-slate-600">{change}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - Call to Action */}
                <div className="px-[20mm] pb-[15mm]">
                    <div className="bg-slate-900 rounded-2xl p-6 flex items-center justify-between">
                        <div className="text-white">
                            <p className="text-lg font-medium mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Ready to Transform Your Smile?</p>
                            <p className="text-sm text-slate-400">Contact us to schedule your consultation</p>
                        </div>
                        <div className="flex items-center gap-4">
                            {clinicSettings?.clinicWebsite && (
                                <div className="bg-white p-2 rounded-lg">
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(clinicSettings.clinicWebsite)}&color=${primaryColor.replace('#', '')}`}
                                        alt="QR Code"
                                        className="w-16 h-16"
                                    />
                                </div>
                            )}
                            <div className="text-right text-white">
                                {clinicSettings?.clinicPhone && <p className="text-sm font-medium">{clinicSettings.clinicPhone}</p>}
                                {clinicSettings?.clinicEmail && <p className="text-xs text-slate-400">{clinicSettings.clinicEmail}</p>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-widest">
                            <FuturaSmileIcon className="w-3 h-3" />
                            <span>Powered by FuturaSmile.com</span>
                        </div>
                        <p className="text-[10px] text-slate-400">Page 4 of 4</p>
                    </div>
                </div>
            </Page>
        </div>
    );
};

export default BrochurePDF;
